import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Building2,
  Send,
  Layers,
  Rocket,
  Briefcase,
  ArrowUpRight,
} from "lucide-react";

// Change this import if you're using an alias
import { Eyebrow } from "../components/SectionHeader";

const AUDIENCES = [
  {
    icon: Building2,
    tag: "Banks",
    title: "Digital Banks & Neobanks",
    body:
      "Extend your customers' reach into new markets and currencies without building cross-border infrastructure in-house.",
  },
  {
    icon: Send,
    tag: "MTOs",
    title: "Money Transfer Operators",
    body:
      "Expand corridor coverage and payout methods while simplifying your technical footprint to a single connection.",
  },
  {
    icon: Layers,
    tag: "PSPs",
    title: "Payment Service Providers",
    body:
      "Add global reach to your existing platform and offer your merchants and customers more ways to send and receive funds.",
  },
  {
    icon: Rocket,
    tag: "Fintechs",
    title: "Fintechs & Platforms",
    body:
      "Whether you're a marketplace, gig-economy platform, or embedded finance product, plug in global payouts and collections without diverting engineering resources from your core product.",
  },
  {
    icon: Briefcase,
    tag: "Corporates",
    title: "Corporates with Cross-Border Payment Needs",
    body:
      "Streamline supplier payments, payroll, and disbursements across multiple countries through one integration.",
  },
];

export default function WhoWeServe() {
  return (
    <div>
      {/* Hero */}
      <section className="pt-40 pb-16 md:pb-24 bg-[color:var(--p10-ivory)] border-b border-[color:var(--p10-line)]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <Eyebrow>Who we serve</Eyebrow>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="font-serif-display text-5xl sm:text-6xl lg:text-8xl leading-[0.98] mt-6 max-w-5xl"
          >
            Built for the businesses that{" "}
            <em className="text-[color:var(--p10-royal)]">
              move money globally
            </em>
            .
          </motion.h1>
        </div>
      </section>

      {/* Cards */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-6">
          {/* Row 1 */}
          {AUDIENCES.slice(0, 2).map((item, index) => {
            const Icon = item.icon;

            return (
              <motion.article
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7, delay: index * 0.1 }}
                data-testid={`audience-card-${item.tag.toLowerCase()}`}
                className={`${
                  index === 0 ? "md:col-span-8" : "md:col-span-4"
                } p-10 md:p-12 border border-[color:var(--p10-line)] bg-white hover:bg-[color:var(--p10-ivory)] transition-colors`}
              >
                <div className="flex items-start justify-between mb-8">
                  <Icon className="w-8 h-8 text-[color:var(--p10-royal)]" />
                  <span className="eyebrow">{item.tag}</span>
                </div>

                <h3 className="font-serif-display text-3xl md:text-4xl leading-tight">
                  {item.title}
                </h3>

                <p className="mt-5 text-[color:var(--p10-ink)]/70 leading-relaxed">
                  {item.body}
                </p>
              </motion.article>
            );
          })}

          {/* Row 2 */}
          {AUDIENCES.slice(2, 4).map((item, index) => {
            const Icon = item.icon;

            return (
              <motion.article
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7, delay: index * 0.1 }}
                data-testid={`audience-card-${item.tag.toLowerCase()}`}
                className={`${
                  index === 0 ? "md:col-span-4" : "md:col-span-8"
                } p-10 md:p-12 border border-[color:var(--p10-line)] bg-white hover:bg-[color:var(--p10-ivory)] transition-colors`}
              >
                <div className="flex items-start justify-between mb-8">
                  <Icon className="w-8 h-8 text-[color:var(--p10-royal)]" />
                  <span className="eyebrow">{item.tag}</span>
                </div>

                <h3 className="font-serif-display text-3xl md:text-4xl leading-tight">
                  {item.title}
                </h3>

                <p className="mt-5 text-[color:var(--p10-ink)]/70 leading-relaxed">
                  {item.body}
                </p>
              </motion.article>
            );
          })}

          {/* Row 3 */}
          {AUDIENCES.slice(4).map((item) => {
            const Icon = item.icon;

            return (
              <motion.article
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7 }}
                data-testid={`audience-card-${item.tag.toLowerCase()}`}
                className="md:col-span-12 p-10 md:p-14 border border-[color:var(--p10-line)] bg-[color:var(--p10-ink)] text-white relative overflow-hidden"
              >
                <div className="grain"></div>

                <div className="relative grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
                  <div className="md:col-span-8">
                    <div className="flex items-center gap-4 mb-6">
                      <Icon className="w-8 h-8 text-[color:var(--p10-electric)]" />
                      <span className="eyebrow-dark">{item.tag}</span>
                    </div>

                    <h3 className="font-serif-display text-3xl md:text-5xl leading-tight">
                      {item.title}
                    </h3>

                    <p className="mt-5 text-white/70 text-lg leading-relaxed max-w-2xl">
                      {item.body}
                    </p>
                  </div>

                  <div className="md:col-span-4 md:justify-self-end">
                    <Link
                      to="/contact"
                      data-testid="who-corp-cta"
                      className="btn-square btn-electric inline-flex items-center"
                    >
                      Talk to Sales
                      <ArrowUpRight className="ml-2 w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </motion.article>
            );
          })}
        </div>
      </section>
    </div>
  );
}